import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { safeToLowerCase } from "../../../../../shared-functions/string";
import { getCurrentProjectID } from "../../../../../app_shared_functions";

import { useDispatch, useSelector } from "react-redux";
import { getProjectsObject } from "../../../../../selectors/projects";
import useZoneId from "../../../../../custom-hooks/region/useZoneId";
import { TypedThunkDispatch } from "../../../../../reducers/thunk/type";
import { closeSlidingMenuLayer } from "../../../../../actions/toggleSlidingMenu";
import { PredefinedParams } from "../../../../../components/slidingpanel/type";
import { createSubnet } from "../../actions";

type SubnetPayload = {
  name: string;
  network_id: string;
  ip_version: string;
  cidr?: string;
  subnetpool_id?: string;
  ipv6_address_mode?: string;
  ipv6_ra_mode?: string;
};

const useSubnetCreateAction = ({
  formData,
  predefined_params,
  createAnother,
}: {
  formData: { [key: string]: any };
  predefined_params?: PredefinedParams;
  createAnother: boolean;
}) => {
  const [isCreating, setIsCreating] = useState(false);

  const projects = useSelector(getProjectsObject);
  const dispatch: TypedThunkDispatch = useDispatch();
  const zoneId = useZoneId(formData.region || predefined_params?.region);

  const handleSubnetCreation = useCallback(() => {
    setIsCreating(true);
    const region = safeToLowerCase(
      formData.region || predefined_params?.region,
    );
    const projectId = getCurrentProjectID(projects, region) || "";

    const subnetPayload: { subnet: SubnetPayload } = {
      subnet: {
        name: formData.name,
        network_id: formData.selectedNetwork,
        ip_version: formData.ipVersion,
      },
    };

    if (formData.ipVersion === "4") {
      subnetPayload.subnet.cidr =
        formData.cidr || (formData.region ? `10.${zoneId}.0.0/24` : "");
    } else {
      subnetPayload.subnet.subnetpool_id = formData.selectedSubnetPoolId;
      subnetPayload.subnet.ipv6_address_mode = formData.addressingType;
      subnetPayload.subnet.ipv6_ra_mode = formData.addressingType;
    }

    dispatch(createSubnet(region, projectId, subnetPayload))
      .then(() => {
        toast.success("Creating network started...");
        if (!createAnother) {
          dispatch(closeSlidingMenuLayer());
        }
      })
      .catch((err) => {
        console.error("Subnet creation failed", err);
      })
      .finally(() => {
        setIsCreating(false);
      });
  }, [
    formData,
    predefined_params?.region,
    createAnother,
    dispatch,
    zoneId,
    projects,
  ]);

  return {
    handleSubnetCreation,
    isCreating,
  };
};

export default useSubnetCreateAction;
